import React, { useState, useEffect } from "react";
import "./css/UserManagement.css";
import { apiFetch as fetch } from "apiClient";

export default function UserManagementPage() {
    const [session, setSession] = useState(null);
    const [users, setUsers] = useState([]);
    const [loadingSession, setLoadingSession] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [error, setError] = useState(null);

    const [newUser, setNewUser] = useState({
        username: "",
        password: "",
        role: "USER",
    });

    const [changePassword, setChangePassword] = useState({
        oldPassword: "",
        newPassword: "",
    });

    // ---- Derived flags ----
    const role = session?.role || session?.appRole || session?.userRole || null;
    const username = session?.username || session?.userName || session?.user || "";
    const isAdmin = role === "ADMIN";

    // ---- Load session on mount ----
    useEffect(() => {
        setLoadingSession(true);
        setError(null);

        fetch("/api/session/status", { credentials: "include" })
            .then(async (r) => {
                if (!r.ok) {
                    throw new Error(`Session check failed: ${r.status}`);
                }
                return r.json();
            })
            .then((data) => {
                console.log("UserManagement session:", data);
                setSession(data);
                if (data.role === "ADMIN") {
                    loadUsers();
                }
            })
            .catch((e) => {
                console.error(e);
                setError("Could not load session information.");
            })
            .finally(() => setLoadingSession(false));
    }, []);

    // ---- Load all users (admin only) ----
    function loadUsers() {
        setLoadingUsers(true);
        setError(null);

        fetch("/api/users", { credentials: "include" })
            .then(async (r) => {
                if (!r.ok) {
                    throw new Error(`Failed to load users: ${r.status}`);
                }
                return r.json();
            })
            .then((data) => setUsers(data))
            .catch((e) => {
                console.error(e);
                setError("Failed to load users.");
            })
            .finally(() => setLoadingUsers(false));
    }

    // ---- Create new user (admin) ----
    function handleCreateUser() {
        if (!newUser.username.trim() || !newUser.password.trim()) {
            alert("Username and password are required.");
            return;
        }

        fetch("/api/users", {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(newUser),
        })
            .then(async (r) => {
                if (!r.ok) {
                    const txt = await r.text();
                    throw new Error(txt || "Failed to create user");
                }
                return r.json();
            })
            .then(() => {
                setNewUser({ username: "", password: "", role: "USER" });
                loadUsers();
            })
            .catch((e) => {
                console.error(e);
                alert(e.message || "Failed to create user.");
            });
    }

    // ---- Delete user (admin) ----
    function handleDelete(id) {
        if (!window.confirm("Delete this user?")) return;

        fetch(`/api/users/${id}`, {
            method: "DELETE",
            credentials: "include",
        })
            .then(async (r) => {
                if (!r.ok) {
                    let message = "Failed to delete user.";

                    try {
                        const contentType = r.headers.get("content-type") || "";

                        if (contentType.includes("application/json")) {
                            const data = await r.json();
                            // Spring Boot 3+ usually puts it in "detail", older style in "message"
                            message = data.detail || data.message || message;
                        } else {
                            const text = await r.text();
                            if (text && text.trim().length > 0) {
                                message = text;
                            }
                        }
                    } catch (parseErr) {
                        console.error("Error parsing delete error response:", parseErr);
                    }

                    throw new Error(message);
                }

                // Success
                loadUsers();
            })
            .catch((e) => {
                console.error(e);
                alert(e.message || "Failed to delete user.");
            });
    }

    // ---- Admin reset password for another user ----
    function resetPassword(id, username) {
        const newPass = window.prompt(`Enter new password for user "${username}":`);
        if (!newPass) return;

        fetch(`/api/users/${id}/password`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ oldPassword: null, newPassword: newPass }),
        })
            .then((r) => {
                if (!r.ok) throw new Error("Failed to reset password");
                alert("Password reset successfully.");
            })
            .catch((e) => {
                console.error(e);
                alert(e.message || "Failed to reset password.");
            });
    }

    // ---- Change own password (any user) ----
    function changeOwnPassword() {
        if (!changePassword.oldPassword.trim() || !changePassword.newPassword.trim()) {
            alert("Both old and new passwords are required.");
            return;
        }

        fetch(`/api/users/me/password`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(changePassword),
        })
            .then((r) => {
                if (!r.ok) throw new Error("Failed to change password");
                alert("Password changed successfully.");
                setChangePassword({ oldPassword: "", newPassword: "" });
            })
            .catch((e) => {
                console.error(e);
                alert(e.message || "Failed to change password.");
            });
    }

    // ---- Render ----
    if (loadingSession) {
        return (
            <div className="user-page">
                <div className="um-card um-card--center">
                    <div className="um-spinner" />
                    <div className="um-muted-text">Loading session…</div>
                </div>
            </div>
        );
    }

    return (
        <div className="user-page">
            <div className="um-header">
                <div>
                    <h1 className="um-title">User Management</h1>
                    <p className="um-subtitle">
                        Manage application users and passwords.
                    </p>
                </div>
                {role && (
                    <div className="um-chip">
                        <span className="um-chip-label">Logged in as</span>
                        <span className="um-chip-value">{username || "Unknown"}</span>
                        <span
                            className={
                                "um-role-badge " +
                                (isAdmin ? "um-role-badge--admin" : "um-role-badge--user")
                            }
                        >
                            {role}
                        </span>
                    </div>
                )}
            </div>

            {error && <div className="um-error">{error}</div>}

            <div className="um-layout">
                {/* LEFT: Admin panel (only visible if ADMIN) */}
                {isAdmin && (
                    <div className="um-card">
                        <div className="um-card-header">
                            <h2>All Users</h2>
                            <button className="um-button um-button-ghost" onClick={loadUsers}>
                                Refresh
                            </button>
                        </div>

                        <div className="um-card-section">
                            <h3 className="um-section-title">Create New User</h3>
                            <div className="um-form-grid">
                                <div className="um-field">
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        value={newUser.username}
                                        onChange={(e) =>
                                            setNewUser({ ...newUser, username: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="um-field">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={newUser.password}
                                        onChange={(e) =>
                                            setNewUser({ ...newUser, password: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="um-field">
                                    <label>Role</label>
                                    <select
                                        value={newUser.role}
                                        onChange={(e) =>
                                            setNewUser({ ...newUser, role: e.target.value })
                                        }
                                    >
                                        <option value="USER">USER</option>
                                        <option value="ADMIN">ADMIN</option>
                                    </select>
                                </div>
                                <div className="um-actions-row">
                                    <button
                                        className="um-button um-button-primary"
                                        onClick={handleCreateUser}
                                    >
                                        Create User
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="um-card-section">
                            <h3 className="um-section-title">Existing Users</h3>

                            {loadingUsers ? (
                                <div className="um-muted-text">Loading users…</div>
                            ) : users.length === 0 ? (
                                <div className="um-muted-text">No users found.</div>
                            ) : (
                                <table className="um-table">
                                    <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Role</th>
                                        <th className="um-col-actions">Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id}>
                                            <td>{u.username}</td>
                                            <td>
                                                <span
                                                    className={
                                                        "um-role-badge " +
                                                        (u.role === "ADMIN"
                                                            ? "um-role-badge--admin"
                                                            : "um-role-badge--user")
                                                    }
                                                >
                                                    {u.role}
                                                </span>
                                            </td>
                                            <td className="um-col-actions">
                                                <button
                                                    className="um-button um-button-ghost"
                                                    onClick={() => resetPassword(u.id, u.username)}
                                                >
                                                    Reset Password
                                                </button>
                                                <button
                                                    className="um-button um-button-danger"
                                                    onClick={() => handleDelete(u.id)}
                                                    disabled={u.username === "admin"}
                                                    title={u.username === "admin" ? "Default admin cannot be deleted" : ""}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}

                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* RIGHT: Change own password (all users) */}
                <div className="um-card">
                    <div className="um-card-header">
                        <h2>Change Your Password</h2>
                    </div>
                    <div className="um-card-section um-form-grid">
                        <div className="um-field">
                            <label>Old password</label>
                            <input
                                type="password"
                                value={changePassword.oldPassword}
                                onChange={(e) =>
                                    setChangePassword({
                                        ...changePassword,
                                        oldPassword: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="um-field">
                            <label>New password</label>
                            <input
                                type="password"
                                value={changePassword.newPassword}
                                onChange={(e) =>
                                    setChangePassword({
                                        ...changePassword,
                                        newPassword: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="um-actions-row">
                            <button
                                className="um-button um-button-primary"
                                onClick={changeOwnPassword}
                            >
                                Update Password
                            </button>
                        </div>

                        {!isAdmin && (
                            <p className="um-muted-text">
                                You can only change your own password. Contact an administrator
                                for any other user changes.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
